/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ControlledInput,
  ControlledMonthYearPicker,
  ControlledSelect,
  FormInput,
  PrimaryButton,
  RedButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useParcels } from "../../store/useParcels";
import { useCouriers } from "../../store/useCouriers";
import { useDropLocations } from "../../store/useDropLocations";
import { FLOAT_REGEX } from "../../utils/regexValidations";

type Inputs = {
  courier: string;
  location: string;
  date: Date;
  volume: number;
  revenue: number;
};

function EditParcelScreen() {
  /** Hooks */
  const params = useParams<{ id: string }>();
  const {
    updateParcel,
    getOneParcel,
    oneParcel,
    updatedParcel,
    clearUpdatedParcel,
  } = useParcels();
  const { allCouriers, getAllCouriers, addCourier } = useCouriers();
  const { dropLocations, getDropLocations } = useDropLocations();
  const navigate = useNavigate();
  const {
    control,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  /** State */
  const [modal, setShowModal] = useState(false);
  const [courierName, setCourierName] = useState("");
  const [submitted, setSubmitted] = useState(false);

  /** Effects */
  useEffect(() => {
    getAllCouriers();
    getDropLocations();
  }, []);

  useEffect(() => {
    if (params?.id) {
      getOneParcel(params.id);
    }
  }, [params?.id]);

  useEffect(() => {
    if (updatedParcel) {
      clearUpdatedParcel();
      navigate(-1);
    }
  }, [updatedParcel]);

  useEffect(() => {
    if (oneParcel?._id) {
      reset({
        // @ts-ignore
        courier: oneParcel?.courier?._id,
        // @ts-ignore
        location: oneParcel?.location?._id,
        date: new Date(oneParcel?.date),
        volume: oneParcel?.volume,
        revenue: oneParcel?.revenue / 100,
      });
    }
  }, [oneParcel]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const addNewCourier = () => {
    if (!courierName || courierName === "") {
      setSubmitted(true);
      return;
    }
    setSubmitted(false);
    addCourier({ name: courierName });
    setCourierName("");
    setShowModal(false);
  };

  const onSubmit = async (data: Inputs) => {
    updateParcel(params.id as string, {
      ...data,
      date: new Date(data?.date.setHours(12, 0, 0, 0)),
      revenue: parseFloat(data.revenue.toString()) * 100,
      volume: parseFloat(data.volume.toString()),
    });
  };

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new Parcel
        </h1>
      </div>
      <div className="flex flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New Parcel
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            <div className="flex flex-row justify-between items-center">
              <ControlledSelect
                name="courier"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.courier)}
                label="Courier"
                options={
                  allCouriers?.length
                    ? allCouriers?.map((courier) => ({
                      title: courier.name as string,
                      _id: courier._id as string,
                    }))
                    : [{ title: "No couriers available", _id: "" }]
                }
                placeholder="Pick Courier here"
                inputClassName="w-full"
                className="w-full"
              />

              <button
                type="button"
                onClick={() => setShowModal(true)}
                className={`w-[100px] h-[46px] px-4 rounded font-satoshi text-sm font-medium bg-blue-500 text-white hover:bg-blue-700 ml-2 mt-4`}
              >
                Add
              </button>
            </div>
            <ControlledMonthYearPicker
              name="date"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.date)}
              label="Date"
              placeholder="Add Date here..."
            />
            <ControlledInput
              control={control}
              label="Volume (number of parcels)"
              placeholder="Number of parcels"
              name="volume"
              type="text"
              rules={{ required: true, pattern: FLOAT_REGEX }}
              error={Boolean(
                errors?.volume ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("volume") ?? 0)
              )}
            />
            <ControlledInput
              control={control}
              label="Revenue (euro)"
              placeholder="Revenue in euro"
              name="revenue"
              rules={{ required: true, pattern: FLOAT_REGEX }}
              error={Boolean(
                errors?.revenue ||
                // @ts-ignore
                !FLOAT_REGEX.test(watch("revenue") ?? 0)
              )}
            />
            <ControlledSelect
              name="location"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.location)}
              label="Location"
              options={dropLocations?.map((location) => ({
                title: location?.cost_place as string,
                _id: location._id as string,
              }))}
              placeholder="Pick location here"
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
      </div>
      {/* Payout modal */}
      {modal ? (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-10/12 sm:w-1/4 my-0 sm:my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-start justify-center p-5 border-b border-solid border-blueGray-200 rounded-t">
                  <h3 className="text-3xl font-semibold font-satoshi text-dark">
                    Add new Courier
                  </h3>
                </div>
                {/*body*/}
                <div className="relative p-6 flex-auto">
                  <FormInput
                    label="Name"
                    placeholder="Courier name"
                    value={courierName}
                    onChange={(e) => setCourierName(e.target.value)}
                  />
                  {courierName === "" && submitted ? (
                    <p className="font-satoshi text-red-600 font-medium">
                      Name is required*
                    </p>
                  ) : null}
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                  <RedButton
                    label="Cancel"
                    className="mr-4"
                    onClick={() => {
                      setCourierName("");
                      setSubmitted(false);
                      setShowModal(false);
                    }}
                  />
                  <PrimaryButton
                    disabled={!courierName || courierName === ""}
                    label="Add"
                    onClick={addNewCourier}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}
    </div>
  );
}

export default withProtectedRoute(EditParcelScreen);
